/**
 * Fleet analytics and performance estimates, per physical aircraft.
 *
 * Every per-aircraft figure is joined through the airframe register's slot
 * history, so two aircraft that shared a drone id are two rows, not one. The
 * server answers only over flights the caller could open one at a time.
 *
 * The performance figures are planning estimates from past flights. They are
 * not the live return margin (api/endurance.ts), and the screens that show
 * them say so, with the number of flights behind each one.
 */

import { api, type ApiResult } from '@/api/client';

export interface Tally {
  flights: number;
  hours: number;
  distanceKm: number;
  errors: number;
  warnings: number;
  breaches: number;
  linkLost: number;
  errorsPerHour: number | null;
  lastFlight: number | null;
}

export interface AirframeReliability extends Tally {
  itemId: string;
  name: string;
  platform: string | null;
  kind: string;
  simulated: boolean;
  retired: boolean;
}

export interface PlatformReliability extends Tally {
  platformId: string;
  platform: string;
  airframes: number;
}

export interface TimelineDay {
  day: number;
  flights: number;
  hours: number;
  warnings: number;
  errors: number;
  breaches: number;
  linkLost: number;
}

export interface AnalyticsSummary {
  window: { days: number; since: number; until: number };
  includeSimulated: boolean;
  outcomes: {
    totals: {
      flights: number;
      hours: number;
      distanceKm: number;
      warnings: number;
      errors: number;
      breaches: number;
      linkLost: number;
      cleanFlights: number;
      peakAltitude: number;
      peakSpeed: number;
      errorsPerHour: number | null;
    };
    byReason: Array<{ reason: string; label: string; count: number; linkLost: boolean }>;
    completion: { withMission: number; planned: number; completed: number; rate: number | null };
  };
  timeline: TimelineDay[];
  linkLost: {
    count: number;
    recent: Array<{
      sessionId: string;
      droneId: string;
      itemId: string | null;
      airframe: string | null;
      startTime: number;
      reason: string;
    }>;
  };
  reliability: {
    airframes: AirframeReliability[];
    platforms: PlatformReliability[];
    unattributed: Tally;
  };
  componentCorrelation: Array<{
    category: string;
    make: string;
    model: string;
    airframes: number;
    flights: number;
    hours: number;
    errors: number;
    warnings: number;
    errorsPerHour: number | null;
  }>;
  activity: {
    byTeam: Array<{ team: string; flights: number; hours: number; lastFlight: number }>;
    byPilot: Array<{ pilot: string; flights: number; hours: number; lastFlight: number }>;
    byDroneId: Array<{ droneId: string; flights: number; hours: number; lastFlight: number }>;
    idleAirframes: Array<{
      itemId: string;
      name: string;
      platform: string | null;
      lastSeen: number | null;
      simulated: boolean;
    }>;
  };
  links: {
    airframes: Array<{
      itemId: string;
      name: string;
      flights: number;
      linkLost: number;
      worstPacketLoss: number | null;
      meanWorstPacketLoss: number | null;
    }>;
    commands: Array<{
      command: string;
      sent: number;
      accepted: number;
      rejected: number;
      rejectRate: number | null;
    }>;
    topFailures: Array<{ command: string; reason: string; count: number }>;
  };
  maintenance: {
    serviceHours: number;
    components: Array<{
      componentId: string;
      itemId: string;
      airframe: string;
      category: string;
      label: string;
      serial: string;
      fittedAt: number;
      flights: number;
      hours: number;
      serviceHours: number;
      fractionOfService: number | null;
      due: boolean;
    }>;
    airframes: Array<{
      itemId: string;
      name: string;
      loggedHours: number;
      registerHours: number;
      flights: number;
    }>;
  };
  events: {
    bySeverity: Array<{ severity: string; count: number }>;
    byType: Array<{ eventType: string; severity: string; count: number }>;
    recent: Array<{
      timestamp: number;
      eventType: string;
      severity: string;
      message: string | null;
      sessionId: string;
      droneId: string;
    }>;
  };
}

export interface EnergyModel {
  itemId: string;
  name: string;
  platform: string | null;
  flights: number;
  /** Flights with energy and distance, the sample the range rests on. */
  samples: number;
  whPerKm: number | null;
  whPerKmSd: number | null;
  whPerMin: number | null;
  whPerMinSd: number | null;
  packWh: number | null;
  packSource: 'spec' | 'measured' | null;
  packSamples: number;
  reservePct: number;
  usableWh: number | null;
  /** The conservative edge: usable energy over the high end of consumption. */
  rangeKm: number | null;
  rangeMeanKm: number | null;
  enduranceMin: number | null;
  enduranceMeanMin: number | null;
  confidence: string;
}

export interface PackHealth {
  componentId: string;
  itemId: string;
  airframe: string | null;
  label: string;
  serial: string;
  fittedAt: number;
  cycles: number;
  ratedWh: number | null;
  measuredWh: number | null;
  maxCellSpread: number | null;
  resistanceMohm: number | null;
  capacityTrendPct: number | null;
  history: Array<{ t: number; wh: number }>;
  note: string | null;
}

export interface SpecComparison {
  platformId: string;
  platform: string;
  flights: number;
  performance: Array<{
    metric: string;
    unit: string;
    claimed: number;
    modelled: number | null;
    percentOfClaim: number | null;
    airframes: number;
  }>;
  capability: Array<{
    metric: string;
    unit: string;
    claimed: number;
    observed: number;
    exceeded: boolean;
  }>;
  exceeded: string[];
}

export interface PerformanceReport {
  basis: string;
  includeSimulated: boolean;
  energy: EnergyModel[];
  wind: { samples: number; costPerMs: number | null; r: number | null; note: string | null };
  temperature: {
    bands: Array<{
      fromC: number;
      toC: number;
      flights: number;
      relativeCost: number;
      relativeRange: number;
    }>;
    referenceC: number | null;
    note: string | null;
  };
  packs: PackHealth[];
  specs: SpecComparison[];
  baselines: Array<{
    itemId: string;
    name: string;
    flights: number;
    metrics: Array<{
      metric: string;
      label: string;
      mean: number | null;
      sd: number | null;
      samples: number;
    }>;
  }>;
  anomalies: Array<{
    sessionId: string;
    itemId: string;
    name: string | null;
    startTime: number;
    metric: string;
    label: string;
    value: number;
    baseline: number;
    sigma: number;
    samples: number;
    endReason: string;
  }>;
}

export interface RangeIntoWind {
  basis: string;
  itemId: string;
  headwindMs?: number;
  rangeKm: number | null;
  stillAirKm?: number | null;
  whPerKm?: number;
  samples?: number;
  windSamples?: number;
  note: string | null;
}

export function getAnalytics(
  days: number,
  includeSimulated: boolean,
): Promise<ApiResult<AnalyticsSummary>> {
  const query = new URLSearchParams({ days: String(days), includeSimulated: String(includeSimulated) });
  return api.get<AnalyticsSummary>(`/analytics?${query.toString()}`, { logged: false });
}

export function getPerformance(includeSimulated: boolean): Promise<ApiResult<PerformanceReport>> {
  return api.get<PerformanceReport>(
    `/analytics/performance?includeSimulated=${String(includeSimulated)}`,
    { logged: false },
  );
}

export function getRangeIntoWind(
  itemId: string,
  headwindMs: number,
  includeSimulated: boolean,
): Promise<ApiResult<RangeIntoWind>> {
  const query = new URLSearchParams({
    itemId,
    headwind: String(headwindMs),
    includeSimulated: String(includeSimulated),
  });
  return api.get<RangeIntoWind>(`/analytics/performance/range?${query.toString()}`, {
    logged: false,
  });
}
